import { Component } from '@theme/component';

class SwfEventAnnouncement extends Component {
  requiredRefs = [];

  #timer = null;
  #active = 0;
  #paused = false;

  connectedCallback() {
    super.connectedCallback();

    const slides = this.#slides();
    if (slides.length === 0) return;

    this.#activate(0);

    const eyebrows = this.#eyebrows();
    if (slides.length <= 1 && eyebrows.length <= 1) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.addEventListener('mouseenter', this.#onPause);
    this.addEventListener('mouseleave', this.#onResume);
    this.addEventListener('focusin', this.#onPause);
    this.addEventListener('focusout', this.#onResume);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.removeEventListener('mouseenter', this.#onPause);
    this.removeEventListener('mouseleave', this.#onResume);
    this.removeEventListener('focusin', this.#onPause);
    this.removeEventListener('focusout', this.#onResume);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #slides() {
    return Array.from(this.querySelectorAll('.swf-event-announcement__slide'));
  }

  #eyebrows() {
    return Array.from(this.querySelectorAll('[data-eyebrow-text]'));
  }

  #cta() {
    return this.querySelector('[data-bar-link]') || this.querySelector('[data-cta]');
  }

  #interval() {
    const v = parseInt(this.dataset.interval || '5000', 10);
    return Number.isFinite(v) && v >= 1000 ? v : 5000;
  }

  #start() {
    this.#stop();
    this.#timer = window.setInterval(() => {
      if (this.#paused) return;
      this.#advance();
    }, this.#interval());
  }

  #stop() {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #advance() {
    const slides = this.#slides();
    if (slides.length > 1) {
      const next = (this.#active + 1) % slides.length;
      this.#activate(next);
    }
    this.#advanceEyebrow();
  }

  #advanceEyebrow() {
    const eyebrows = this.#eyebrows();
    if (eyebrows.length <= 1) return;
    const activeIdx = eyebrows.findIndex((e) => e.hasAttribute('data-active'));
    const nextIdx = (activeIdx + 1) % eyebrows.length;
    eyebrows.forEach((e) => e.removeAttribute('data-active'));
    eyebrows[nextIdx].setAttribute('data-active', '');
  }

  #activate(idx) {
    const slides = this.#slides();
    if (!slides[idx]) return;

    slides.forEach((s) => s.removeAttribute('data-active'));
    slides[idx].setAttribute('data-active', '');
    this.#active = idx;

    const tile = slides[idx].dataset.tile || '';
    if (tile) {
      this.style.setProperty('--swf-ann-active-tile', `url("${tile}")`);
      this.removeAttribute('data-no-tile');
    } else {
      this.style.setProperty('--swf-ann-active-tile', 'none');
      this.setAttribute('data-no-tile', '');
    }

    const bgColor = slides[idx].dataset.bgColor || '';
    if (bgColor) {
      this.style.setProperty('--swf-ann-active-bg', bgColor);
    } else {
      this.style.removeProperty('--swf-ann-active-bg');
    }

    const link = slides[idx].dataset.link || '';
    const cta = this.#cta();
    if (cta) {
      if (link) {
        cta.setAttribute('href', link);
        cta.removeAttribute('aria-hidden');
        cta.style.pointerEvents = '';
      } else {
        cta.setAttribute('href', '#');
        cta.setAttribute('aria-hidden', 'true');
        cta.style.pointerEvents = 'none';
      }
    }
  }

  #onPause = () => {
    this.#paused = true;
  };

  #onResume = () => {
    this.#paused = false;
  };

  #onVisibility = () => {
    this.#paused = document.hidden;
  };
}

if (!customElements.get('swf-event-announcement')) {
  customElements.define('swf-event-announcement', SwfEventAnnouncement);
}
